"use client";

import { useAdminOptional } from "@/lib/admin/context";
import styles from "./AdminPendingBar.module.css";

/**
 * AdminPendingBar — sticky preloader top-of-page widoczny podczas każdego
 * aktywnego żądania w panelu administracyjnym (Server Action, fetch).
 *
 * **Implementacja:** subscribuje do `pendingRequests` z `AdminContext`.
 * Komponent renderuje:
 *   - Bar z animowanym progress fill (indeterminate) gdy `pendingRequests > 0`
 *   - Tekstowy komentarz `Pracuję… (N)` po prawej (N = liczba aktywnych
 *     requestów) — admin wie ile rzeczy się dzieje równolegle
 *   - Nic (return null) gdy idle
 *
 * **Pozycja:** absolutna, z-index ponad AdminTopBar i AlignmentChecker —
 * widoczna z dowolnego miejsca w admin layout.
 *
 * **Standard:** każda akcja admin panelu wywołująca `Server Action` lub
 * `fetch` powinna iść przez `withPendingRequest` z AdminContext — wtedy bar
 * automatycznie się pokazuje + ukrywa.
 */
export function AdminPendingBar() {
  const admin = useAdminOptional();
  if (!admin) return null;

  const { pendingRequests, t } = admin;
  if (pendingRequests === 0) return null;

  return (
    <div
      className={styles.bar}
      role="status"
      aria-live="polite"
      aria-label={t.pendingBar.ariaLabel(pendingRequests)}
    >
      <div className={styles.progress}>
        <div className={styles.progressFill} />
      </div>
      <span className={styles.label}>
        {t.pendingBar.statusLabel(pendingRequests)}
      </span>
    </div>
  );
}
